import type { Schema, SchemaField } from '@cli-types/core-engine/schema.js'
import type { CardGroup, GroupAnalysis } from '@cli-types/next-adapter/forms.js'
import { singularize, toCamelCase, toPascalCase } from '@cli-utils/text.js'
import { buildDefaultValueFromSyncFunctions } from '@next-adapter/generators/form/form-shared/build-default-value-from-sync-functions.js'
import { buildDefaultValues } from '@next-adapter/generators/form/form-shared/build-default-values.js'
import { buildZodFields } from '@next-adapter/generators/form/form-shared/build-zod-fields.js'
import { collectDefaultValueFromSourceNames } from '@next-adapter/generators/form/form-shared/collect-default-value-from-source-names.js'
import { renderSingleFormCardComponentRenderer } from '@next-adapter/templates/renderers/generators/form/form-single/generate-card-component.js'
import { buildGroupCreatableSelectHandlers } from './build-group-creatable-select-handlers.js'
import { buildGroupCreatableSelectState } from './build-group-creatable-select-state.js'
import { buildGroupFieldArrayHooks } from './build-group-field-array-hooks.js'
import { buildGroupFieldsJSX } from './build-group-fields-jsx.js'
import { buildGroupRelState } from './build-group-rel-state.js'
import { buildGroupSelectState } from './build-group-select-state.js'

export function generateCardComponent(
  group: CardGroup,
  schema: Schema,
  analysis: GroupAnalysis,
  allFlatFields: SchemaField[]
): string {
  const singular = singularize(schema.name)
  const Singular = toPascalCase(singular)

  const zodFields = buildZodFields(group.flatFields)
  const defaultValues = buildDefaultValues(group.flatFields, 'initialData', allFlatFields)
  const defaultValueFromSourceNames = collectDefaultValueFromSourceNames(group.flatFields)
  const defaultValueFromSyncFunctions = buildDefaultValueFromSyncFunctions(group.flatFields)
  const creatableSelectFields = group.flatFields.filter(
    (field) => field.type === 'select' && field.creatable === true
  )
  const creatableSelectFieldNames = new Set(creatableSelectFields.map((field) => field.name))
  const fieldsJSX = buildGroupFieldsJSX(
    group,
    analysis,
    defaultValueFromSourceNames,
    creatableSelectFieldNames,
    schema.name
  )
  const relState = buildGroupRelState(analysis)
  const selectState = buildGroupSelectState(
    analysis.multiSelectFields.filter((field) => field.creatable !== true)
  )
  const creatableSelectState = buildGroupCreatableSelectState(
    creatableSelectFields,
    schema,
    Singular
  )
  const creatableSelectHandlers = buildGroupCreatableSelectHandlers(
    creatableSelectFields,
    schema,
    Singular,
    defaultValueFromSourceNames
  )
  const fieldArrayHooks = buildGroupFieldArrayHooks(analysis)
  const savedValueName = `saved${Singular}`
  const resetValues = buildDefaultValues(group.flatFields, savedValueName, allFlatFields).replace(
    /^ {6}/gm,
    '        '
  )

  const descriptionLine = group.description
    ? `\n            <CardDescription>${group.description}</CardDescription>`
    : ''
  const tabField = group.fields.find((field) => field.type === 'tabs')
  const tabNames = tabField?.tabs?.map((tab) => tab.name) ?? []
  const tabParserConst = analysis.hasTabsField
    ? renderSingleFormCardComponentRenderer({
        kind: 'renderCardComponentTabValuesTemplate',
        props: {
          groupVarPrefix: group.varPrefix,
          map: tabNames.map((name) => JSON.stringify(name)).join(', '),
          toPascalCase: toPascalCase(group.varPrefix)
        }
      })
    : ''

  return renderSingleFormCardComponentRenderer({
    kind: 'renderCardComponentCardHeaderTemplate',
    props: {
      groupVarPrefix: group.varPrefix,
      zodFields: zodFields,
      tabParserConst: tabParserConst,
      groupComponentName: group.componentName,
      singular: Singular,
      analysisHasTabsFieldTab: analysis.hasTabsField
        ? `\n  const [activeTab, setActiveTab] = useQueryState('tab', ${group.varPrefix}TabParser)`
        : '',
      relStateContent: relState ? `\n${relState}` : '',
      creatableSelectStateContent: creatableSelectState ? `\n${creatableSelectState}` : '',
      selectStateContent: selectState ? `\n${selectState}` : '',
      defaultValues: defaultValues,
      savedValueName: savedValueName,
      toCamelCase: toCamelCase(singular),
      resetValues: resetValues,
      groupTitle: group.title,
      schemaName: schema.name,
      defaultValueFromSyncFunctionsContent: defaultValueFromSyncFunctions
        ? `\n${defaultValueFromSyncFunctions}\n`
        : '',
      fieldArrayHooksContent: fieldArrayHooks ? `\n${fieldArrayHooks}\n` : '',
      creatableSelectHandlersContent: creatableSelectHandlers
        ? `${creatableSelectHandlers}\n\n`
        : '',
      descriptionLine: descriptionLine,
      fieldsJsx: fieldsJSX,
      groupHintText: group.hint
        ? `\n            <p className="text-sm text-muted-foreground">${group.hint}</p>`
        : ''
    }
  })
}
